import VideoPlayer from "@dana/Components/VideoPlayer";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { headings } from "@dana/MobiusOverrides/styles/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { AutocompleteApiParameter, autocompleteSearch } from "@insite/client-framework/Services/AutocompleteService";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Badge, { BadgeTypeValues } from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { ReactNode, useState } from "react";
import { css } from "styled-components";

interface OpeningScreenProps {
    fields: any;
    onProceedToNextScreen?: () => void;
}

export interface EZIDOpeningStyles {
    introWrapper?: InjectableCss;
    introImage?: ImgProps;
    introContent?: InjectableCss;
    heading?: TypographyPresentationProps;
    headingWrapper?: InjectableCss;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    bottomText?: TypographyPresentationProps;

    openingWrapper?: InjectableCss;
    openingLeftWrapper?: InjectableCss;
    openingCenterWrapper?: InjectableCss;
    openingRightWrapper?: InjectableCss;
    openingLeftImage?: ImgProps;
    openingRightImage?: ImgProps;
    enterPartNumberText?: TypographyPresentationProps;
    noPartNumberText?: TypographyPresentationProps;
    clickHereButton?: ButtonPresentationProps;
    noPartNumberRow?: InjectableCss;
    openingVideoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const eZIDOpeningStyles: EZIDOpeningStyles = {
    introWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            width: 100%;
            background: ${themeBackgrounds.lightGray};
            padding: 48px 32px;
            gap: 48px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        // xs: mobile
                        css`
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 16px;
                        padding: 20px 8px;
                    `,
                        // sm: small tablet
                        css`
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 20px;
                        padding: 28px 12px;
                    `,
                        // md: iPad/tablet
                        css`
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 28px;
                        padding: 36px 20px;
                    `,
                        // lg/xl: desktop
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    introImage: {
        css: css`
            width: 300px;
            max-width: 300px;
            max-height: 300px;
            aspect-ratio: 1/1;
            flex-shrink: 0;
        `,
    },
    introContent: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
            max-width: 1100px;
        `,
    },
    heading: {
        variant: "h1",
        css: css`
            letter-spacing: -0.68px;
            align-self: stretch;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 16px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${headings.h1.fontSizeSmall};
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    headingWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
        `,
    },
    subText: {
        variant: "h2",
        css: css`
            letter-spacing: -0.68px;
            align-self: stretch;
            margin-bottom: 12px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            ${headings.h2.fontSizeSmall};
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    description: {
        css: css`
            letter-spacing: -0.68px;
            align-self: stretch;
            margin-bottom: 16px;
        `,
    },
    bottomText: {
        variant: "h3",
        css: css`
            letter-spacing: -0.68px;
            align-self: stretch;
            margin-top: 8px;
        `,
    },
    openingWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: stretch;
            justify-content: center;
            width: 100%;
            gap: 24px;
            margin: 48px 0 0 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            gap: 16px;
                            margin: 24px 0 0 0;
                        `,
                        css`
                            flex-direction: column;
                            gap: 20px;
                            margin: 32px 0 0 0;
                        `,
                        css`
                            flex-direction: column;
                            gap: 24px;
                            margin: 40px 0 0 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    openingLeftWrapper: {
        css: css`
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 0;
        min-width: 0;
        max-width: none;
        height: 100%;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            max-width: 100%;
                            margin-bottom: 0;
                        `,
                        null,
                        null,
                        css`
                            width: auto;
                        `,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    openingCenterWrapper: {
        css: css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1 1 0;
        min-width: 0;
        max-width: none;
        padding: 0 16px;
        gap: 16px;

        & > *:not(:last-child) {
            margin-bottom: 12px;
        }

        button {
            min-width: 120px;
        }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 4px;
                        `,
                        css`
                            padding: 0 8px;
                        `,
                        css`
                            padding: 0 12px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    openingRightWrapper: {
        css: css`
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 0;
        min-width: 0;
        max-width: none;
        height: 100%;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            max-width: 100%;
                            margin-bottom: 0;
                        `,
                        null,
                        null,
                        css`
                            width: auto;
                        `,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    openingLeftImage: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 320px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    openingRightImage: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 320px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 120px;
                        `,
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    enterPartNumberText: {
        variant: "h4",
        css: css`
            text-align: center;
        `,
    },
    noPartNumberText: {
        variant: "h3",
        css: css`
            text-align: center;
        `,
    },
    clickHereButton: {
        variant: "tertiary",
        css: css`
            margin-bottom: 30px;
        `,
    },
    noPartNumberRow: {
        css: css`
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        justify-content: center;
        width: 100%;
    `,
    },
    openingVideoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const OpeningScreen: React.FC<OpeningScreenProps> = ({
    fields, onProceedToNextScreen
}) => {
    const styles = eZIDOpeningStyles;
    const [openingVideoModalIsOpen, setOpeningVideoModalIsOpen] = useState(false);
    const openingVideoUrl = useGetLink(fields.openingVideo);
    const openingVideoCaption = useGetLink(fields.openingVideoCaption);
    const [partNumberSearchText, setPartNumberSearchText] = useState("");
    const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const notFoundMessage = translate("Your Search for '{0}' has found 0 results.");

    const openingVideoModalCloseHandler = () => {
        setOpeningVideoModalIsOpen(false);
    };

    const searchPartNumber = async () => {
        if (!partNumberSearchText.trim() || errorMessage) {
            return;
        }
        setShowLoadingOverlay(true);
        try {
            const autocompleteApiParameter = {
                query: partNumberSearchText
            } as AutocompleteApiParameter;
            const products = (await autocompleteSearch(autocompleteApiParameter)).products;
            // Validate result
            const query = partNumberSearchText.trim().toLowerCase();
            const matchedProduct = products?.find(product => {
                const brandName = product.brandName?.toLowerCase() || "";
                const erpNumber = product.erpNumber?.toLowerCase() || "";
                const manufacturerItemNumber = product.manufacturerItemNumber?.toLowerCase() || "";

                if (brandName !== "spicer readyshaft") {return false;}

                if (!erpNumber.endsWith("r")) {return false;}

                if (
                    erpNumber === query ||
                    erpNumber === `${query}r` ||
                    manufacturerItemNumber === query
                ) {
                    return true;
                }
                return false;
            });

            if (matchedProduct) {
                window.location.assign(matchedProduct.url);
                return;
            }

            setErrorMessage(notFoundMessage.replace("{0}", partNumberSearchText));
            setShowLoadingOverlay(false);

        } catch (error) {
            console.error("Error searching part number:", error);
            setErrorMessage(notFoundMessage.replace("{0}", partNumberSearchText));
            setShowLoadingOverlay(false);
        }
    };

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                        width: 100%;
                    `}
        >
            <StyledWrapper>
                <StyledWrapper {...styles.introWrapper} className="wrapper">
                    {fields.pageImage && (
                        <Img src={fields.pageImage} altText={fields.pageImageAltText || ''} {...styles.introImage} />
                    )}
                    <StyledWrapper {...styles.introContent}>
                        {fields.pageHeading && (
                            <StyledWrapper {...styles.headingWrapper}>
                                <Typography {...styles.heading} as="h1">
                                    {fields.pageHeading}
                                </Typography>
                                <Badge type={BadgeTypeValues.Text} value={fields.patentPending} badgeTextRounding={20} />
                            </StyledWrapper>
                        )}
                        {fields.pageSubText && (<Typography {...styles.subText} as="h2">{fields.pageSubText}</Typography>)}
                        {fields.pageDescription && (
                            <Typography className="text-body-lg" {...styles.description}>
                                {parse(fields.pageDescription, parserOptions)}
                            </Typography>
                        )}
                        {fields.pageBottomText && (<Typography {...styles.bottomText} as="h3">{fields.pageBottomText}</Typography>)}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.openingWrapper} className="wrapper">
                    <StyledWrapper {...styles.openingLeftWrapper}>
                        {fields.openingLeftImage && (
                            <Img src={fields.openingLeftImage} altText={fields.openingLeftAltText || ''} {...styles.openingLeftImage} />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.openingCenterWrapper}>
                        <StyledWrapper {...styles.noPartNumberRow}>
                            {fields.noPartNumberLabel && (
                                <Typography {...styles.noPartNumberText} as="h3">
                                    {fields.noPartNumberLabel}
                                </Typography>
                            )}
                            {fields.clickHereLabel && (
                                <Button {...styles.clickHereButton} onClick={onProceedToNextScreen}>
                                    {fields.clickHereLabel}
                                </Button>
                            )}
                        </StyledWrapper>
                        {fields.enterPartNumberLabel && (
                            <Typography {...styles.enterPartNumberText} as="h4">
                                {fields.enterPartNumberLabel}
                            </Typography>
                        )}
                        <TextField
                            className="part-number-text-field"
                            id="partNumberTextField"
                            value={partNumberSearchText}
                            onChange={e => {
                                setErrorMessage("");
                                setPartNumberSearchText(e.target.value);
                            }}
                            error={errorMessage}
                        />
                        <Button variant="primary" onClick={searchPartNumber}>
                            {fields.submitButtonLabel}
                        </Button>
                        {openingVideoUrl?.url && (
                            <Button {...styles.watchVideoButton} onClick={() => setOpeningVideoModalIsOpen(true)}>
                                {fields.watchVideoLabel}
                            </Button>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.openingRightWrapper}>
                        {fields.openingRightImage && (
                            <Img src={fields.openingRightImage} altText={fields.openingRightAltText || ''} {...styles.openingRightImage} />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <Modal
                    {...styles.openingVideoModal}
                    id="openingVideoModal"
                    isOpen={openingVideoModalIsOpen}
                    handleClose={openingVideoModalCloseHandler}>
                    <StyledWrapper>
                        <VideoPlayer
                            videoUrl={openingVideoUrl?.url || ''}
                            videoCaptionUrl={openingVideoCaption?.url || ''}
                        />
                    </StyledWrapper>
                </Modal>
            </StyledWrapper>
        </LoadingOverlay>
    )
};

export default OpeningScreen;